import { PlayerModule } from './player/player.module';
import { BrowserModule } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER, ErrorHandler, Optional, Inject } from '@angular/core';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { routes } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthModule } from './auth/auth.module';
import { GtagModule } from './gtag/gtag.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, PathLocationStrategy } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { JwtInterceptor } from './auth/helpers/jwt.interceptor';
import { ErrorInterceptor } from './auth/helpers/error.interceptor';
import { LanguageInterceptor } from './shared/interceptors/language.interceptor';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { LogInterceptor } from './shared/interceptors/log.interceptor';
import { SharedModule } from './shared/shared.module';
import { ModalModule } from 'ngx-bootstrap/modal';
import { RecaptchaModule, RecaptchaFormsModule } from 'ng-recaptcha';
import { NgxJsonLdModule } from '@ngx-lite/json-ld';
import { TransferHttpCacheInterceptor } from './shared/interceptors/http-transfer.interceptor';
import { PopoverModule } from 'ngx-bootstrap/popover';
import * as Sentry from '@sentry/angular';
import { SENTRY, SENTRY_TRACING } from './shared/interfaces/sentry.interface';
import { TimeoutInterceptor, DEFAULT_TIMEOUT } from './shared/interceptors/timeout.interceptor';


import {
  L10nTranslationModule,
  L10nIntlModule,
  L10nLoader,
  L10nRoutingModule,
  L10nTranslationService,
  L10nTranslationLoader,
} from '@snack-dev/angular-l10n';
import {
  l10nConfig,
  initL10n,
  HttpTranslationLoader,
  MissingTranslationHandler,
  l10nPreload,
  StoreTranslationHandler,
  UserLanguage,
} from './localization.module';
import { environment } from 'src/environments/environment';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { BrowserCookiesModule } from './cookies/browser/browser-cookies.module';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { BrowserTransferStateModule } from './shared/platform/browser/transfer-state';

const originalPrepareExternalUrl = PathLocationStrategy.prototype.prepareExternalUrl;
PathLocationStrategy.prototype.prepareExternalUrl = function(internal) {
  const url = originalPrepareExternalUrl.call(this, internal);
  if (url === '') {
    return url;
  }
  if (url.endsWith('/')) {
    return url;
  }
  return PathUtils.addTrailingSlash(url);
};

class PathUtils {

  // Para la parte de ? o #
  static firstOccurrence(firstOption: string, secondOption: string, url: string) {
    const firstIndex = url.indexOf(firstOption);
    const secondIndex = url.indexOf(secondOption);
    if (firstIndex === -1 && secondIndex !== -1) {
      return secondOption;
    } else if (firstIndex !== -1 && secondIndex === -1) {
      return firstOption;
    } else if (firstIndex === -1 && secondIndex === -1) {
      return null;
    } else {
      if (firstIndex < secondIndex) {
        return firstOption;
      } else {
        return secondOption;
      }
    }
  }

  static addTrailingSlash(path: string) {
    // chequear si tiene ? o #
    const occurrence = PathUtils.firstOccurrence('?', '#', path);
    if (occurrence) {
      // caso en el q es query string o #
      const splitted = path.split(occurrence, 2);
      const firstElement = splitted.shift();
      const joiner = firstElement.endsWith('/') ? '' : '/';
      const finalPath = firstElement + joiner + occurrence + splitted.join();
      return finalPath;
    } else {
      if (path.endsWith('/')) {
        path = path.slice(0, -1);
      }
      return path + '/';
    }
  }

}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule.withServerTransition({ appId: 'serverApp' }),
    BrowserTransferStateModule,
    BrowserCookiesModule.forRoot(),
    BrowserAnimationsModule,
    // External libs
    RecaptchaModule,
    RecaptchaFormsModule,
    NgxJsonLdModule,
    L10nTranslationModule.forRoot(l10nConfig, {
      translationLoader: HttpTranslationLoader,
      missingTranslationHandler: MissingTranslationHandler,
      storage: StoreTranslationHandler,
      userLanguage: UserLanguage,
    }),
    L10nIntlModule,
    L10nRoutingModule.forRoot(),
    // Ngx-bootstrap
    PaginationModule.forRoot(),
    BsDropdownModule.forRoot(),
    PopoverModule.forRoot(),
    ModalModule.forRoot(),
    CarouselModule.forRoot(),
    TooltipModule.forRoot(),
    // Internal
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    AuthModule,
    CommonModule,
    SharedModule,
    PlayerModule,
    GtagModule.init({ targetId: environment.gtag }),
    RouterModule.forRoot(routes, {
      onSameUrlNavigation: 'reload',
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'enabled',
      initialNavigation: 'enabled',
    }),
  ],
  providers: [
    { provide: SENTRY, useValue: Sentry },
    { provide: ErrorHandler, useValue: Sentry.createErrorHandler({ showDialog: false }), },
    { provide: SENTRY_TRACING, useClass: Sentry.TraceService, deps: [Router], },
    TransferHttpCacheInterceptor,
    { provide: HTTP_INTERCEPTORS, useClass: LogInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true },
    // { provide: HTTP_INTERCEPTORS, useClass: TimeoutInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useExisting: TransferHttpCacheInterceptor, multi: true },
    { provide: DEFAULT_TIMEOUT, useValue: 60000 },
    {
      provide: APP_INITIALIZER,
      useFactory: initL10n,
      deps: [L10nLoader],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: l10nPreload,
      deps: [L10nTranslationService, L10nTranslationLoader],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(@Optional() @Inject(SENTRY_TRACING) trace: any) {
  }
}
